import { For, Show, createMemo } from "solid-js";
import { MeasureRow } from "./MeasureRow";
import { linkClick } from "../core/router";
import { collectionFor, garmentType } from "../core/catalogue";
import { specifications } from "@tailor-frame/patterns";
import { formatOptionValue, type ChoiceDefinition, type OptionDefinition, type OptionValue, type PatternProject, type PatternSpecification, type PresetDefinition } from "@tailor-frame/engine";

interface Props {
  specification: PatternSpecification;
  project: PatternProject;
  measurementsHref: string;
  pieceCount?: number;
  onOption: (id: string, value: OptionValue) => void;
  onIncludeInlay: (include: boolean) => void;
}

/**
 * Style options beside the workspace canvas, so every change redrafts the
 * pieces (and the body overlay showing the ease) while the user watches.
 * Groups start collapsed; the user opens the ones they want to adjust.
 */
export function StylePanel(props: Props) {
  // Options a pattern fixes (hidden, e.g. by a module use) are not offered.
  const visibleOptions = createMemo(() => Object.entries(props.specification.options).filter(([, definition]) => !definition.hidden));
  const groups = createMemo(() => {
    const grouped = new Map<string, Array<[string, OptionDefinition]>>();
    for (const entry of visibleOptions()) {
      const group = entry[1].group ?? "Style";
      grouped.set(group, [...(grouped.get(group) ?? []), entry]);
    }
    return [...grouped.entries()];
  });
  const changed = (id: string) => props.project.options[id] !== props.specification.options[id]?.default;
  const changedCount = createMemo(() => visibleOptions().map(([id]) => id).filter(changed).length);
  const hasInlays = createMemo(() => Object.values(props.specification.blocks).some((block) => Object.values(block.pieces ?? {}).some((piece) => (piece.inlays?.length ?? 0) > 0)));
  const reset = (ids: string[]) => { for (const id of ids) if (changed(id)) props.onOption(id, props.specification.options[id].default); };
  const resetTitle = (definition: OptionDefinition) => `Reset to ${formatOptionValue(definition, definition.default)}`;
  // A preset is selected while every option it sets still holds its value.
  const presets = () => props.specification.presets ?? [];
  const presetMatches = (preset: PresetDefinition) => Object.entries(preset.options).every(([id, value]) => String(props.project.options[id]) === String(value));
  const applyPreset = (preset: PresetDefinition) => { for (const [id, value] of Object.entries(preset.options)) if (props.specification.options[id]) props.onOption(id, value); };

  // The same details the select screen shows for the pattern, so they stay to hand while styling.
  const pattern = () => props.specification.pattern;
  const entry = createMemo(() => specifications.find((item) => item.specification.pattern.id === pattern().id));

  return (
    <aside class="style-panel" aria-label="Style options">
      <header class="style-panel-heading">
        <p class="eyebrow">{entry() ? `${collectionFor(entry()!).name} · ${garmentType(entry()!)}` : pattern().category ?? "STYLE"}</p>
        <h2>{pattern().name}</h2>
        <dl class="inspector-facts style-panel-facts">
          <div><dt>Version</dt><dd>v{pattern().version}</dd></div>
          <div><dt>Pieces</dt><dd>{props.pieceCount ?? "–"}</dd></div>
          <Show when={entry()}>{(found) => <div><dt>Source</dt><dd>{collectionFor(found()).source}</dd></div>}</Show>
        </dl>
        <Show when={pattern().description}><p class="inspector-description style-panel-description">{pattern().description}</p></Show>
        <Show when={pattern().makes?.length}>
          <div class="inspector-makes style-panel-makes">
            <h3>Makes</h3>
            <ul><For each={pattern().makes}>{(garment) => <li>{garment}</li>}</For></ul>
          </div>
        </Show>
        <p class="style-made-for">Cut for <strong>{props.project.madeFor}</strong> · <a href={props.measurementsHref} onClick={linkClick}>measurements</a></p>
        <div class="style-panel-summary">
          <span>{changedCount() === 0 ? "All defaults" : `${changedCount()} changed`}</span>
          <Show when={changedCount() > 0}><button type="button" class="link-button" onClick={() => reset(visibleOptions().map(([id]) => id))}>Reset all</button></Show>
        </div>
      </header>

      <div class="style-panel-body">
        <Show when={presets().length > 0}>
          <div class="style-presets" role="group" aria-label="Fit presets">
            <p class="choice-label">Fit preset</p>
            <For each={presets()}>{(preset) => (
              <button type="button" classList={{ selected: presetMatches(preset) }} aria-pressed={presetMatches(preset)} title={preset.description} onClick={() => applyPreset(preset)}>{preset.label}</button>
            )}</For>
          </div>
        </Show>
        <Show when={groups().length > 0} fallback={<p class="fit-hint">This pattern has no style options.</p>}>
          <For each={groups()}>{([group, entries]) => (
            <details class="style-group">
              <summary>{group}<small>{entries.filter(([id]) => changed(id)).length || ""}</small></summary>
              <For each={entries}>{([id, definition]) => (
                <div class="style-option" classList={{ changed: changed(id) }}>
                  {definition.type === "choice"
                    ? <ChoiceControl id={id} definition={definition} value={props.project.options[id]} onChange={(value) => props.onOption(id, value)} />
                    : definition.type === "toggle"
                      ? <label class="check option-toggle"><input type="checkbox" checked={props.project.options[id] === 1} onChange={(event) => props.onOption(id, event.currentTarget.checked ? 1 : 0)} /> {definition.label}</label>
                      : <MeasureRow id={id} definition={definition} value={Number(props.project.options[id])} active={false} onActive={() => undefined} onChange={props.onOption} />}
                  <Show when={changed(id)}><button type="button" class="link-button reset-option" classList={{ "reset-choice": definition.type === "choice" }} title={resetTitle(definition)} onClick={() => reset([id])}>reset</button></Show>
                </div>
              )}</For>
            </details>
          )}</For>
        </Show>
        <Show when={hasInlays()}>
          <details class="style-group">
            <summary>Bespoke</summary>
            <div class="style-option" classList={{ changed: !!props.project.includeInlay }}>
              <label class="check option-toggle"><input type="checkbox" checked={!!props.project.includeInlay} onChange={(event) => props.onIncludeInlay(event.currentTarget.checked)} /> Include inlay for bespoke adjustment</label>
              <p class="fit-hint inlay-hint"><span class="inlay-swatch" aria-hidden="true" />Extra cloth beyond the seam allowance at seams a tailor may let out, such as the centre back, side seams and sleeve length.</p>
            </div>
          </details>
        </Show>
      </div>
    </aside>
  );
}

/**
 * A choice: buttons for up to four choices, otherwise a dropdown.
 */
function ChoiceControl(props: { id: string; definition: ChoiceDefinition; value: OptionValue | undefined; onChange: (value: string) => void }) {
  const keys = () => Object.keys(props.definition.choices);
  const current = () => keys().includes(String(props.value)) ? String(props.value) : props.definition.default;
  return (
    <div class="choice-option" role="group" aria-labelledby={`choice-${props.id}`}>
      <label id={`choice-${props.id}`} class="choice-label" for={keys().length > 4 ? `choice-select-${props.id}` : undefined}>{props.definition.label}</label>
      <Show when={keys().length > 4} fallback={
        <div class="segmented-control" classList={{ "three-up": keys().length === 3 }}>
          <For each={keys()}>{(key) => <button type="button" classList={{ selected: current() === key }} aria-pressed={current() === key} onClick={() => props.onChange(key)}>{props.definition.choices[key]}</button>}</For>
        </div>
      }>
        <select id={`choice-select-${props.id}`} class="choice-select" value={current()} onChange={(event) => props.onChange(event.currentTarget.value)}>
          <For each={keys()}>{(key) => <option value={key} selected={current() === key}>{props.definition.choices[key]}</option>}</For>
        </select>
      </Show>
    </div>
  );
}
